import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "../../i18n";
import { apiFetch } from "../../api/apiFetch";
import { normalizePosition, positionLabelKey, type Position } from "../../access/positions";
import AccessLevelCell from "./AccessLevelCell";
import { AccessRules, LevelOrInherit, cycleLevel, effectiveLevel, storedLevel, isGated } from "./acHelpers";
import { AccessControlConfig, LoginUserRow } from "./types";

export interface UsersViewProps {
    config: AccessControlConfig;
    /** Rules with any in-progress edits already overlaid. */
    rules: AccessRules;
    onCycle: (uid: string, warehouse: string | null, key: string, next: LevelOrInherit) => void;
}

const UsersView = ({ config, rules, onCycle }: UsersViewProps) => {
    const { t } = useTranslation();
    const [users, setUsers] = useState<LoginUserRow[]>([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState("");
    const [selectedUid, setSelectedUid] = useState<number | null>(null);

    useEffect(() => {
        let alive = true;
        (async () => {
            setLoading(true);
            try {
                const res = await apiFetch("/login/users", { method: "GET" });
                if (!res.ok) return;
                const data = await res.json();
                if (alive && Array.isArray(data)) setUsers(data);
            } finally {
                if (alive) setLoading(false);
            }
        })();
        return () => { alive = false; };
    }, []);

    const filtered = useMemo(() => {
        const q = search.trim().toLowerCase();
        if (!q) return users;
        return users.filter(u =>
            u.name?.toLowerCase().includes(q)
            || u.warehouse?.toLowerCase().includes(q)
            || String(u.UID).includes(q));
    }, [users, search]);

    const selected = useMemo(() => users.find(u => u.UID === selectedUid) ?? null, [users, selectedUid]);
    const position = selected ? normalizePosition(selected.permissions) : null;

    return (
        <div className="ac-users">
            <div className="ac-users-list-pane">
                <input
                    className="ac-filter-input"
                    type="text"
                    placeholder={t("ac_users_search_placeholder")}
                    value={search}
                    onChange={e => setSearch(e.target.value)}
                    aria-label={t("ac_users_search_placeholder")}
                />
                {loading && <div className="ac-users-loading">{t("loading")}</div>}
                <ul className="ac-users-list">
                    {filtered.map(u => (
                        <li key={u.UID}>
                            <button
                                type="button"
                                className={`ac-users-list-item${selectedUid === u.UID ? " ac-users-list-item--active" : ""}`}
                                onClick={() => setSelectedUid(u.UID)}
                            >
                                <span className="ac-users-list-name">{u.name}</span>
                                <span className="ac-users-list-meta">{u.warehouse} · {u.permissions}</span>
                            </button>
                        </li>
                    ))}
                    {!loading && filtered.length === 0 && (
                        <li className="ac-users-empty">{t("ac_users_none_found")}</li>
                    )}
                </ul>
            </div>

            <div className="ac-users-detail-pane">
                {!selected && <p className="ac-hint">{t("ac_users_select_hint")}</p>}
                {selected && !position && <p className="ac-hint">{t("ac_users_unknown_position")}</p>}
                {selected && position && (
                    <>
                        <p className="ac-hint">
                            {t("ac_users_override_hint", { position: t(positionLabelKey(position as Position)) })}
                        </p>
                        <div className="ac-matrix-scroll">
                            <table className="ac-matrix-table">
                                <thead>
                                    <tr>
                                        <th className="ac-matrix-key-col">{t("ac_key_col")}</th>
                                        <th>{t("ac_defaults_tab")}</th>
                                        <th>{selected.warehouse}</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {config.keys.map(k => (
                                        <tr key={k.key} className="ac-matrix-key-row">
                                            <td className="ac-matrix-key-col">
                                                <div className="ac-key-label"><span>{k.label}</span></div>
                                                <code className="ac-key-code">{k.key}</code>
                                            </td>
                                            {([null, selected.warehouse] as (string | null)[]).map(scope => {
                                                const stored = storedLevel({
                                                    rules, subjectType: "user", subjectId: String(selected.UID), warehouse: scope, key: k.key,
                                                });
                                                const eff = effectiveLevel({
                                                    keyDef: k, rules, position, warehouse: scope, uid: String(selected.UID),
                                                });
                                                return (
                                                    <td key={scope ?? "default"}>
                                                        <AccessLevelCell
                                                            stored={stored}
                                                            effective={eff.level}
                                                            title={stored === "inherit" ? t("ac_inherited_from_position") : t("ac_user_override_beats_position")}
                                                            onCycle={() => onCycle(String(selected.UID), scope, k.key, cycleLevel(stored))}
                                                            gated={isGated(k.key, config.keys, { rules, position, warehouse: scope, uid: String(selected.UID) })}
                                                        />
                                                    </td>
                                                );
                                            })}
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
};

export default UsersView;
